import {useRef, useState, useEffect} from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {SessionMeta, Attachment, ChatMessage} from '../lib/types';
import {brand, fontFamily, space} from '../brand/theme';
import {CONFIG, OCR_MODEL_ID} from '../lib/config';
import {chatTitleFromText, DEFAULT_CHAT_LABEL, sessionModelId} from '../lib/sessions';
import {modelChatPath} from '../lib/modelRoute';
import OcrWorkspace from './OcrWorkspace';

type ConnectorState = {prozorro: boolean; stats: boolean};
type ConnectorKey = keyof ConnectorState;

interface Props {
  session: SessionMeta;
  onUpdateSession: (s: SessionMeta) => void;
  userId: string;
  apiKey?: string;
  onOcrDirtyChange?: (dirty: boolean) => void;
}

const ECOSYSTEM_WIDGET_SRC = 'https://lapathoniia.top/widgets/ecosystem/v1/widget.js?v=20260616-scroll';
const ECOSYSTEM_WIDGET_LOCAL_SRC = '/ecosystem-widget-local.js';
const ECOSYSTEM_WIDGET_API = 'https://lapathoniia.top/api/ecosystem/widgets/top-projects';
const ECOSYSTEM_WIDGET_SAMPLE_API = '/ecosystem-widget-sample.json';
const OCR_IMAGE_REQUIRED_MESSAGE = '⚠️ Для Rukopys OCR прикріпіть зображення рукопису.';

function loadStoredMessages(sessionId: string): ChatMessage[] {
  try {
    const saved = localStorage.getItem(`${CONFIG.storageKeys.chatPrefix}${sessionId}`);
    const parsed = saved ? JSON.parse(saved) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map(message => message?.role === 'assistant' && message.content === OCR_IMAGE_REQUIRED_MESSAGE
      ? {...message, isError: true, connectorHint: undefined}
      : message);
  } catch {
    return [];
  }
}

function isLocalEcosystemWidget() {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
}

function getEcosystemWidgetApiUrl() {
  return isLocalEcosystemWidget() ? ECOSYSTEM_WIDGET_SAMPLE_API : ECOSYSTEM_WIDGET_API;
}

function getEcosystemWidgetScriptUrl() {
  return isLocalEcosystemWidget() ? ECOSYSTEM_WIDGET_LOCAL_SRC : ECOSYSTEM_WIDGET_SRC;
}

function EcosystemShowcase({compact = false}: {compact?: boolean}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const apiUrl = getEcosystemWidgetApiUrl();

    fetch(`${apiUrl}?limit=3&lang=uk`, {headers: {Accept: 'application/json'}, credentials: 'omit'})
      .then(resp => resp.ok ? resp.json() : null)
      .then(payload => {
        if (cancelled) return;
        const projects = Array.isArray(payload?.projects) ? payload.projects : [];
        if (projects.length === 0) return;
        setShouldRender(true);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!shouldRender) return;

    let cancelled = false;
    const apiUrl = getEcosystemWidgetApiUrl();
    const scriptUrl = getEcosystemWidgetScriptUrl();

    const mountWidget = () => {
      if (cancelled || !hostRef.current) return;

      hostRef.current.replaceChildren();
      const widget = document.createElement('lapathoniia-ecosystem-widget');
      widget.setAttribute('lang', 'uk');
      widget.setAttribute('limit', '3');
      widget.setAttribute('api-url', apiUrl);
      if (compact) widget.setAttribute('compact', '');

      hostRef.current.appendChild(widget);
    };

    if (customElements.get('lapathoniia-ecosystem-widget')) {
      mountWidget();
      return () => { cancelled = true; };
    }

    let script = document.querySelector<HTMLScriptElement>(`script[src="${scriptUrl}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      document.head.appendChild(script);
    }

    const onError = () => {
      if (!cancelled) setLoadError(true);
    };

    script.addEventListener('error', onError, {once: true});
    customElements.whenDefined('lapathoniia-ecosystem-widget').then(mountWidget).catch(onError);

    return () => {
      cancelled = true;
      script?.removeEventListener('error', onError);
    };
  }, [shouldRender, compact]);

  if (loadError || !shouldRender) return null;

  return (
    <div
      className={`ecosystem-showcase${compact ? ' ecosystem-showcase--compact' : ''}`}
      ref={hostRef}
      aria-label="Проєкти, створені з Lapathoniia"
    />
  );
}

export default function ChatView({session, onUpdateSession, userId, apiKey, onOcrDirtyChange}: Props) {
  const [model, setModel] = useState<string>(() => sessionModelId(session));
  const [showModelDropdown, setShowModelDropdown] = useState(false);
  const [webSearch, setWebSearch] = useState(true);
  const [deepResearch, setDeepResearch] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [connectors, setConnectors] = useState<ConnectorState>(() => {
    try { const s = localStorage.getItem('lapathoniia_connectors'); return s ? JSON.parse(s) : {prozorro: false, stats: false}; } catch { return {prozorro: false, stats: false}; }
  });
  const [showConnectorSub, setShowConnectorSub] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<ConnectorInfo | null>(null);
  const [infoConnector, setInfoConnector] = useState<ConnectorInfo | null>(null);
  const [showPlusMenu, setShowPlusMenu] = useState(false);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<ChatMessage[]>(() => loadStoredMessages(session.id));
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isResearchStreaming, setIsResearchStreaming] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string>(session.id);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const modelDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      const toSave = messages.map(m => ({...m, images: m.images?.map(() => ''), files: m.files?.map(f => ({name: f.name, content: ''}))}));
      localStorage.setItem(`${CONFIG.storageKeys.chatPrefix}${session.id}`, JSON.stringify(toSave));
    } catch {}
  }, [messages, session.id]);

  useEffect(() => {
    if (connectors.prozorro || connectors.stats) {
      setWebSearch(false);
      setDeepResearch(false);
    }
  }, []);

  // Mobile detection
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Reload messages when switching sessions
  useEffect(() => {
    // Save current session's messages before switching
    // (the messages save effect may not have fired yet)
    const prevSessionId = sessionIdRef.current;
    if (prevSessionId && prevSessionId !== session.id) {
      try {
        const toSave = messages.map(m => ({...m, images: m.images?.map(() => ''), files: m.files?.map(f => ({name: f.name, content: ''}))}));
        localStorage.setItem(`${CONFIG.storageKeys.chatPrefix}${prevSessionId}`, JSON.stringify(toSave));
      } catch {}
      // Abort stream and save progress when switching sessions
      abortRef.current?.abort();
    }
    sessionIdRef.current = session.id;
    setMessages(loadStoredMessages(session.id));
    setModel(sessionModelId(session));
    setIsStreaming(false);
    setIsResearchStreaming(false);
  }, [session.id]);

  useEffect(() => { messagesEndRef.current?.scrollIntoView({behavior: isStreaming ? 'smooth' : 'auto' as ScrollBehavior}); }, [messages]);

  // Auto-focus input on mount and after streaming
  useEffect(() => { setTimeout(() => inputRef.current?.focus(), 50); }, [session.id]);

  // Warn before page refresh during streaming
  useEffect(() => {
    if (!isStreaming) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isStreaming]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (showPlusMenu && dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowPlusMenu(false);
        setShowConnectorSub(false);
      }
      if (showModelDropdown && modelDropdownRef.current && !modelDropdownRef.current.contains(e.target as Node)) setShowModelDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler as any);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('touchstart', handler as any); };
  }, [showPlusMenu, showModelDropdown]);

  const handleFileChange = (type: 'image' | 'file') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = (reader.result as string).split(',')[1];
        setAttachments(prev => [...prev, {type, name: file.name, mimeType: file.type, base64}]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
    setShowPlusMenu(false);
  };

  const removeAttachment = (idx: number) => setAttachments(prev => prev.filter((_, i) => i !== idx));

  const sendMessage = async () => {
    if (!input.trim() && attachments.length === 0) return;
    const currentAttachments = [...attachments];
    const imageAtts = currentAttachments.filter(a => a.type === 'image');
    const fileAtts = currentAttachments.filter(a => a.type === 'file');
    const msgText = input;
    const userMsg: ChatMessage = {role: 'user', content: input, images: imageAtts.map(a => a.base64), files: fileAtts.map(a => ({name: a.name, content: a.base64}))};
    setMessages(prev => [...prev, userMsg]);
    if (session.label === DEFAULT_CHAT_LABEL) {
      const title = chatTitleFromText(msgText || currentAttachments[0]?.name || '');
      if (title) onUpdateSession({...session, label: title});
    }
    const isDeepReq = deepResearch;
    const connectorHint = connectors.prozorro ? 'prozorro' : connectors.stats ? 'stats' : undefined;
    setInput('');
    setAttachments([]);
    if (inputRef.current) inputRef.current.style.height = '44px';
    setIsStreaming(true);
    let pendingSources: any[] | null = null;
    let assistantText = '';
    setIsResearchStreaming(isDeepReq);
    if (deepResearch) { setDeepResearch(false); setWebSearch(true); }

    try {
      const body: any = {message: msgText, model, session_id: session.id, user_id: userId, web_search: webSearch, deep_research: deepResearch, connectors: connectors.prozorro || connectors.stats ? connectors : false};
      // Send recent chat history for immediate context (last 6 messages)
      const recentMsgs = messages.filter(m => m.role !== 'system').slice(-6).map(m => ({role: m.role, content: m.content || '', ...(m.sig ? {sig: m.sig} : {})}));
      if (recentMsgs.length > 0) body.chat_history = recentMsgs;
      if (imageAtts.length > 0) body.images = imageAtts.map(a => ({base64: a.base64, mime_type: a.mimeType || 'image/jpeg'}));
      if (fileAtts.length > 0) body.files = fileAtts.map(a => ({name: a.name, content: a.base64}));

      abortRef.current = new AbortController();
      const headers: Record<string, string> = {'Content-Type': 'application/json'};
      if (apiKey) headers.Authorization = `Bearer ${apiKey}`;
      const resp = await fetch(`${CONFIG.backendUrl}/chat/stream`, {method: 'POST', headers, body: JSON.stringify(body), signal: abortRef.current.signal});

      if (!resp.ok) {
        const errText = await resp.text();
        if (resp.status === 429 || resp.status === 401 || errText.toLowerCase().includes('budget')) {
          let resetTime = localStorage.getItem('budget_reset_at');
          if (!resetTime || new Date(resetTime).getTime() < Date.now()) {
            const reset = new Date(Date.now() + 3600000);
            localStorage.setItem('budget_reset_at', reset.toISOString());
            resetTime = reset.toISOString();
          }
          const reset = new Date(resetTime);
          setMessages(prev => [...prev, {role: 'assistant', content: apiKey
            ? `⚠️ Бюджет вашого API ключа вичерпано.\n\n💡 Зверніться до адміністратора для збільшення ліміту.`
            : `⚠️ Спільний бюджет демо-сервісу вичерпано!\n\nЦе тестовий режим — бюджет спільний для всіх користувачів і оновлюється щогодини.\n\n🔄 Наступне оновлення: ~${reset.toLocaleTimeString('uk-UA')}\n\n💡 Спробуйте пізніше. Дякуємо за розуміння!`, isError: true}]);
        } else {
          let detail = errText;
          try { detail = JSON.parse(errText).detail || errText; } catch {}
          if (detail.includes('<html') || detail.includes('<!')) detail = 'Сервер тимчасово недоступний';
          setMessages(prev => [...prev, {role: 'assistant', content: detail, isError: true}]);
        }
        setIsStreaming(false);
        return;
      }

      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      assistantText = '';
      let buffer = '';
      let bubbleAdded = false;

      while (reader) {
        const {done, value} = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, {stream: true});
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') continue;
          try {
            const data = JSON.parse(payload);
            if (data.error === 'budget_exceeded') {
              let resetTime = localStorage.getItem('budget_reset_at');
              if (!resetTime || new Date(resetTime).getTime() < Date.now()) {
                const reset = new Date(Date.now() + 3600000);
                localStorage.setItem('budget_reset_at', reset.toISOString());
                resetTime = reset.toISOString();
              }
              const reset = new Date(resetTime);
              const msg = apiKey
                ? `⚠️ Бюджет вашого API ключа вичерпано.\n\n💡 Зверніться до адміністратора для збільшення ліміту.`
                : `⚠️ Спільний бюджет демо-сервісу вичерпано!\n\nЦе тестовий режим — бюджет спільний для всіх користувачів і оновлюється щогодини.\n\n🔄 Наступне оновлення: ~${reset.toLocaleTimeString('uk-UA')}\n\n💡 Спробуйте пізніше. Дякуємо за розуміння!`;
              if (!bubbleAdded) { setMessages(prev => [...prev, {role:'assistant', content: msg, isError:true}]); }
              else { setMessages(prev => { const u = [...prev]; u[u.length-1] = {role:'assistant', content: msg, isError:true}; return u; }); }
              break;
            }
            if (data.error === 'context_window_exceeded') {
              const msg = '⚠️ Контекстне вікно переповнене!\n\nВаше повідомлення перевищує ліміт моделі.\n\n💡 Скоротіть повідомлення або почніть новий чат.';
              if (!bubbleAdded) { setMessages(prev => [...prev, {role:'assistant', content: msg, isError:true}]); }
              else { setMessages(prev => { const u = [...prev]; u[u.length-1] = {role:'assistant', content: msg, isError:true}; return u; }); }
              break;
            }
            if (data.error) {
              const msg = typeof data.text === 'string' && data.text
                ? data.text
                : '⚠️ Не вдалося отримати відповідь. Спробуйте ще раз.';
              if (!bubbleAdded) {
                setMessages(prev => [...prev, {role: 'assistant', content: msg, isError: true}]);
              } else {
                setMessages(prev => {
                  const updated = [...prev];
                  updated[updated.length - 1] = {role: 'assistant', content: msg, isError: true};
                  return updated;
                });
              }
              assistantText = msg;
              bubbleAdded = true;
              break;
            }
            if (data.text) {
              assistantText += data.text;
              if (!bubbleAdded) {
                setMessages(prev => [...prev, {role: 'assistant', content: assistantText, ...(connectorHint ? {connectorHint} : {})}]);
                bubbleAdded = true;
              } else {
                setMessages(prev => { const u = [...prev]; u[u.length-1] = {...u[u.length-1], role:'assistant', content: assistantText, ...(connectorHint ? {connectorHint} : {})}; return u; });
              }
            }
            if (data.msg_sig) {
              setMessages(prev => { const u = [...prev]; if (u.length > 0 && u[u.length-1].role === 'assistant') { u[u.length-1] = {...u[u.length-1], sig: data.msg_sig}; } return u; });
            }
            if (data.replace_full) {
              setMessages(prev => { const u = [...prev]; if (u.length > 0 && u[u.length-1].role === 'assistant') { u[u.length-1] = {...u[u.length-1], content: data.replace_full, ...(connectorHint ? {connectorHint} : {})}; } return u; });
            }
            if (data.sources) {
              pendingSources = data.sources;
            }
          } catch {}
        }
      }
    } catch (err: any) {
      if (err.name === 'AbortError') { /* user stopped */ }
      else {
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last && last.role === 'assistant' && !last.content) {
            const u = [...prev];
            u[u.length - 1] = {role: 'assistant', content: '⚠️ Сервер тимчасово недоступний.\n\nСпробуйте оновити сторінку або повторити через кілька секунд.', isError: true};
            return u;
          }
          return [...prev, {role: 'assistant', content: '⚠️ Сервер тимчасово недоступний.\n\nСпробуйте оновити сторінку або повторити через кілька секунд.', isError: true}];
        });
      }
    }

    setIsStreaming(false);
    setIsResearchStreaming(false);
    // Attach sources after streaming complete
    if (pendingSources && sessionIdRef.current === session.id) {
      setMessages(prev => { const u = [...prev]; if (u.length > 0 && u[u.length-1].role === 'assistant') { u[u.length-1] = {...u[u.length-1], sources: pendingSources!}; } return u; });
    }
    // Safety net: ensure we never leave user without a response
    setMessages(prev => {
      const last = prev[prev.length - 1];
      if (last && last.role === 'user') {
        // No assistant response was added at all
        return [...prev, {role: 'assistant', content: '⚠️ Не вдалося отримати відповідь. Спробуйте ще раз або почніть новий чат.', isError: true}];
      }
      if (last && last.role === 'assistant' && !last.content && !last.isError) {
        // Assistant bubble exists but empty
        const u = [...prev];
        u[u.length - 1] = {role: 'assistant', content: '⚠️ Відповідь порожня. Спробуйте переформулювати або почніть новий чат.', isError: true, ...(connectorHint ? {connectorHint} : {})};
        return u;
      }
      return prev;
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const selectedModel = CONFIG.models.find(m => m.id === model);
  const selectedModelBadge = selectedModel && 'badge' in selectedModel ? selectedModel.badge : undefined;
  const connectorByKey = (key?: ConnectorKey) => key ? MCP_CONNECTORS.find(connector => connector.stateKey === key) : undefined;
  const activeConnectorCount = MCP_CONNECTORS.filter(connector => connectors[connector.stateKey]).length;
  const hasMessages = messages.length > 0;
  const disableConnectors = () => {
    const next: ConnectorState = {prozorro: false, stats: false};
    setConnectors(next);
    localStorage.setItem('lapathoniia_connectors', JSON.stringify(next));
  };
  const toggleConnector = (connector: ConnectorInfo) => {
    const willEnable = !connectors[connector.stateKey];
    if (willEnable) {
      setWebSearch(false);
      setDeepResearch(false);
    }
    setConnectors(prev => {
      const next: ConnectorState = willEnable
        ? {prozorro: false, stats: false, [connector.stateKey]: true}
        : {...prev, [connector.stateKey]: false};
      localStorage.setItem('lapathoniia_connectors', JSON.stringify(next));
      return next;
    });
    inputRef.current?.focus();
  };

  const selectModel = (nextModel: typeof CONFIG.models[number]) => {
    if (nextModel.id === model) {
      setShowModelDropdown(false);
      return;
    }
    if (nextModel.mode === 'ocr') return;
    setMessages(prev => [...prev, {role: 'system', content: `Модель змінено на ${nextModel.name}`}]);
    setModel(nextModel.id);
    onUpdateSession({...session, mode: 'chat', modelId: nextModel.id});
    setShowModelDropdown(false);
    inputRef.current?.focus();
  };

  if (session.mode === 'ocr' || model === OCR_MODEL_ID) {
    const ocrSession: SessionMeta = session.mode === 'ocr' && session.modelId === OCR_MODEL_ID
      ? session
      : {...session, mode: 'ocr', modelId: OCR_MODEL_ID, label: 'Rukopys OCR'};
    return <OcrWorkspace session={ocrSession} onUpdateSession={onUpdateSession} onDirtyChange={onOcrDirtyChange} userId={userId} apiKey={apiKey} />;
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden', background: `url('/bg-pattern.svg') no-repeat center center / cover, radial-gradient(ellipse at 50% 10%, rgba(232, 200, 48, 0.035) 0%, transparent 45%), radial-gradient(ellipse at 50% 90%, rgba(78, 205, 196, 0.025) 0%, transparent 50%)`}}>
      {/* Messages */}
      <div style={{flex: 1, overflowY: 'auto', minHeight: 0, padding: `${space(3)} ${space(2)}`, display: 'flex', flexDirection: 'column', gap: space(2), WebkitOverflowScrolling: 'touch'}}>
        {hasMessages && <EcosystemShowcase compact />}
        {!hasMessages && (
          <div className="chat-empty-state">
            <EcosystemShowcase />
            <div className="chat-empty-intro">
              <div style={{fontSize: '40px', opacity: 0.8}}>🇺🇦</div>
              <h2 style={{margin: 0, color: brand.cream, fontSize: '18px', fontWeight: 600, letterSpacing: '-0.3px'}}>Lapathoniia Playground</h2>
              <p style={{margin: 0, color: brand.creamMuted, fontSize: '14px', textAlign: 'center', maxWidth: '400px', lineHeight: '1.6'}}>
                Українські LLM — MamayLM та LapaLLM.<br/>
                Задайте питання{CONFIG.enableVision ? ', завантажте зображення або файл' : ' або завантажте файл'} для аналізу.
              </p>
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={{display: 'flex', justifyContent: msg.role === 'system' ? 'center' : msg.role === 'user' ? 'flex-end' : 'flex-start', padding: `0 ${space(1)}`}}>
            {msg.role === 'system' ? (
              <div style={{fontSize: '12px', color: brand.creamSubtle, fontStyle: 'italic'}}>— {msg.content} —</div>
            ) : (
              <div className="msg-bubble" style={{
                maxWidth: '70%', padding: `${space(1.5)} ${space(2)}`,
                borderRadius: msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: msg.isError
                  ? brand.errorMuted
                  : msg.role === 'user'
                    ? 'linear-gradient(135deg, #8B2A15, #B85520, #C48A20)'
                    : 'rgba(30, 66, 72, 0.85)',
                color: msg.isError ? brand.error : brand.cream,
                border: msg.isError ? `1px solid rgba(196, 92, 74, 0.3)` : msg.role === 'assistant' ? `1px solid rgba(244, 237, 212, 0.12)` : 'none',
                fontSize: '14px', lineHeight: '1.65', wordBreak: 'break-word',
              }}>
                {msg.images && msg.images.length > 0 && (
                  <div style={{display: 'flex', gap: '8px', marginBottom: '8px', flexWrap: 'wrap'}}>
                    {msg.images.map((img, j) => img ? (
                      <img key={j} src={`data:image/jpeg;base64,${img}`} alt="" style={{maxWidth: '200px', maxHeight: '150px', borderRadius: '8px'}} />
                    ) : (
                      <span key={j} style={{background: brand.bgMuted, padding: '6px 10px', borderRadius: '8px', fontSize: '12px', color: brand.creamMuted}}>🖼️ Зображення</span>
                    ))}
                  </div>
                )}
                {msg.files && msg.files.length > 0 && (
                  <div style={{display: 'flex', gap: '6px', marginBottom: '8px', flexWrap: 'wrap'}}>
                    {msg.files.map((f, j) => <span key={j} style={{background: brand.bgMuted, padding: '4px 10px', borderRadius: '8px', fontSize: '12px', color: brand.gold}}>📎 {f.name || 'Файл'}</span>)}
                  </div>
                )}
                {msg.role === 'assistant' ? <Markdown remarkPlugins={[remarkGfm]} components={{a: ({...props}) => <a {...props} target="_blank" rel="noopener noreferrer" />}}>{msg.content}</Markdown> : msg.content}
                {msg.role === 'assistant' && msg.sources && msg.sources.length > 0 && (
                  <SourcesPill sources={msg.sources} />
                )}
                {msg.role === 'assistant' && msg.connectorHint && !msg.isError && (!isStreaming || i < messages.length - 1) && connectorByKey(msg.connectorHint) && (
                  <ConnectorHelpPill connector={connectorByKey(msg.connectorHint)!} onClick={() => setInfoConnector(connectorByKey(msg.connectorHint)!)} />
                )}
                {msg.role === 'assistant' && !msg.isError && msg.content && (!isStreaming || i < messages.length - 1) && (
                  <FeedbackButtons sessionId={session.id} model={model} messageContent={msg.content} userMessage={messages[i-1]?.content || ''} />
                )}
              </div>
            )}
          </div>
        ))}
        {isStreaming && (messages.length === 0 || messages[messages.length-1]?.role === 'user') && (
          <div style={{display: 'flex', justifyContent: 'flex-start', padding: `0 ${space(1)}`}}>
            <div style={{padding: `${space(1.5)} ${space(2)}`, borderRadius: '16px 16px 16px 4px', background: brand.bgElevated, border: `1px solid ${brand.borderLight}`, display: 'flex', alignItems: 'center', gap: '10px'}}>
              <span style={{display: 'inline-flex', gap: '4px', alignItems: 'center'}}>
                <span style={{width: '6px', height: '6px', borderRadius: '50%', background: brand.gold, animation: 'blink 1.4s infinite 0s'}}/>
                <span style={{width: '6px', height: '6px', borderRadius: '50%', background: brand.gold, animation: 'blink 1.4s infinite 0.2s'}}/>
                <span style={{width: '6px', height: '6px', borderRadius: '50%', background: brand.gold, animation: 'blink 1.4s infinite 0.4s'}}/>
              </span>
              <ThinkingText isResearch={isResearchStreaming} />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Disclaimer */}
      <div style={{textAlign: 'center', padding: '4px', fontSize: '12px', color: brand.creamMuted, opacity: 0.7, flexShrink: 0}}>
        Моделі можуть помилятися — перевіряйте важливу інформацію.
      </div>

      {/* Attachments */}
      {attachments.length > 0 && (
        <div style={{display: 'flex', justifyContent: 'center', padding: `${space(1)} 16px`}}>
          <div style={{display: 'flex', gap: '8px', flexWrap: 'wrap', width: '100%', maxWidth: '800px'}}>
            {attachments.map((att, i) => (
              <div key={i} style={{display: 'flex', alignItems: 'center', gap: '6px', background: brand.bgMuted, borderRadius: '8px', padding: '6px 12px', fontSize: '12px', color: brand.cream, border: `1px solid ${brand.border}`}}>
                {att.type === 'image' ? '🖼️' : '📎'} {att.name}
                <button onClick={() => removeAttachment(i)} style={{border: 'none', background: 'none', color: brand.error, cursor: 'pointer', fontSize: '13px', padding: 0}}>×</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area — unified card */}
      <div style={{padding: '8px 16px calc(16px + env(safe-area-inset-bottom, 0px))', display: 'flex', justifyContent: 'center', flexShrink: 0}}>
        <div style={{width: '100%', maxWidth: '800px', background: brand.bgElevated, borderRadius: '12px', border: `1px solid ${brand.border}`, boxShadow: '0 2px 20px rgba(0,0,0,0.3)'}}>

          {/* Status row — mobile only when features active */}
          {isMobile && (webSearch || deepResearch || activeConnectorCount > 0) && (
            <div style={{display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderBottom: `1px solid ${brand.borderLight}`}}>
              {webSearch && <button onClick={() => setShowPlusMenu(true)} style={{display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px', border: 'none', background: 'rgba(78,205,196,0.1)', color: '#4ecdc4', fontSize: '11px', fontWeight: 500, fontFamily, cursor: 'pointer'}}>🌐 Веб</button>}
              {deepResearch && <button onClick={() => setShowPlusMenu(true)} style={{display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px', border: 'none', background: 'rgba(240,208,64,0.1)', color: brand.gold, fontSize: '11px', fontWeight: 500, fontFamily, cursor: 'pointer'}}>🔬 Deep</button>}
              {activeConnectorCount > 0 && <button onClick={() => { setShowPlusMenu(true); setShowConnectorSub(true); }} style={{display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', borderRadius: '6px', border: 'none', background: 'rgba(232,112,48,0.1)', color: brand.orange || brand.gold, fontSize: '11px', fontWeight: 500, fontFamily, cursor: 'pointer'}}>🔌 {activeConnectorCount}</button>}
            </div>
          )}

          {/* Input row */}
          <div style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 12px'}}>
            {/* Plus menu */}
            <div ref={dropdownRef} style={{position: 'relative', flexShrink: 0}}>
              <button
                onClick={() => { setShowPlusMenu(prev => { if (prev) setShowConnectorSub(false); return !prev; }); }}
                aria-haspopup="menu" aria-expanded={showPlusMenu} className="touch-target"
                style={{width: '44px', height: '44px', borderRadius: '8px', border: `1px solid ${showPlusMenu ? brand.gold : brand.border}`, background: showPlusMenu ? 'rgba(240,208,64,0.1)' : 'transparent', color: showPlusMenu ? brand.gold : brand.creamMuted, fontSize: '18px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', transition: 'all 0.15s'}}
              >
                {showPlusMenu ? '×' : '+'}
                {!showPlusMenu && (webSearch || deepResearch || activeConnectorCount > 0) && (
                  <span style={{position: 'absolute', top: '6px', right: '6px', width: '7px', height: '7px', borderRadius: '50%', background: brand.gold, border: `1.5px solid ${brand.bgElevated}`}}/>
                )}
              </button>
              {showPlusMenu && (
                <div role="menu" style={{position: 'absolute', bottom: '50px', left: 0, background: brand.bgElevated, border: `1px solid ${brand.border}`, borderRadius: '12px', padding: '6px', width: 'min(340px, calc(100vw - 32px))', boxShadow: '0 18px 52px rgba(0,0,0,0.58)', zIndex: 100}}>
                  <div style={{borderBottom: `1px solid ${brand.borderLight}`, marginBottom: '4px', paddingBottom: '4px'}}>
                    <MenuItem icon="🔌" label={`Конектори ${showConnectorSub ? '▴' : '▾'}`} active={activeConnectorCount > 0} badge={activeConnectorCount > 0 ? `${activeConnectorCount}/2` : '2 MCP'} onClick={() => setShowConnectorSub(prev => !prev)} />
                    {showConnectorSub && (
                      <div style={{padding: '2px 0 4px'}}>
                        {MCP_CONNECTORS.map(connector => (
                          <ConnectorMenuItem
                            key={connector.id}
                            connector={connector}
                            active={connectors[connector.stateKey]}
                            onToggle={() => toggleConnector(connector)}
                            onInfo={() => { setInfoConnector(connector); setShowConnectorSub(false); setShowPlusMenu(false); }}
                            onConfig={() => { setSelectedConnector(connector); setShowConnectorSub(false); setShowPlusMenu(false); }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  {(selectedModel?.vision) && <MenuItem icon="🖼️" label="Завантажити зображення" onClick={() => imageInputRef.current?.click()} />}
                  <MenuItem icon="📎" label="Прикріпити файл" onClick={() => fileInputRef.current?.click()} />
                  <div style={{borderTop: `1px solid ${brand.borderLight}`, marginTop: '4px', paddingTop: '4px'}}>
                    <MenuItem icon="🌐" label="Веб-пошук" active={webSearch} onClick={() => { const willEnable = !webSearch; setWebSearch(willEnable); if (willEnable) { setDeepResearch(false); disableConnectors(); } setShowPlusMenu(false); inputRef.current?.focus(); }} />
                    <MenuItem icon="🔬" label="Глибоке дослідження" active={deepResearch} onClick={() => { const willEnable = !deepResearch; setDeepResearch(willEnable); if (willEnable) { setWebSearch(false); disableConnectors(); } setShowPlusMenu(false); inputRef.current?.focus(); }} />
                  </div>
                </div>
              )}
            </div>

            {/* Textarea */}
            <textarea ref={inputRef} value={input} onChange={e => { setInput(e.target.value); const el = e.target; el.style.height = '44px'; if (el.scrollHeight > 44) { el.style.height = Math.min(el.scrollHeight, 160) + 'px'; el.style.overflowY = el.scrollHeight > 160 ? 'auto' : 'hidden'; } }} onKeyDown={handleKeyDown} placeholder="Повідомлення..." rows={1} style={{flex: 1, resize: 'none', border: 'none', outline: 'none', background: 'transparent', color: brand.cream, fontSize: '14px', fontFamily, padding: '11px 0', lineHeight: '22px', height: '44px', maxHeight: '160px', overflowY: 'hidden', minWidth: 0}} />

            {/* Desktop indicators */}
            {!isMobile && (
              <div style={{display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0}}>
                {webSearch && <button onClick={() => setShowPlusMenu(true)} title="Веб-пошук" style={{width: '28px', height: '28px', borderRadius: '6px', border: 'none', background: 'rgba(78,205,196,0.12)', color: '#4ecdc4', fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>🌐</button>}
                {deepResearch && <button onClick={() => setShowPlusMenu(true)} title="Дослідження" style={{width: '28px', height: '28px', borderRadius: '6px', border: 'none', background: 'rgba(240,208,64,0.12)', color: brand.gold, fontSize: '14px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>🔬</button>}
                {activeConnectorCount > 0 && <button onClick={() => { setShowPlusMenu(true); setShowConnectorSub(true); }} title="Конектори" style={{height: '28px', padding: '0 8px', borderRadius: '6px', border: 'none', background: 'rgba(232,112,48,0.12)', color: brand.orange || brand.gold, fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '3px', fontFamily}}>🔌<span>{activeConnectorCount}</span></button>}
              </div>
            )}

            {/* Model selector — always in input row */}
            <div ref={modelDropdownRef} style={{position: 'relative', flexShrink: 0}}>
              <button onClick={() => setShowModelDropdown(!showModelDropdown)} className="touch-target" style={{height: '44px', padding: '0 10px', borderRadius: '8px', border: `1px solid ${brand.border}`, background: 'transparent', color: brand.creamMuted, fontSize: '12px', cursor: 'pointer', fontFamily, whiteSpace: 'nowrap', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '4px'}}>
                <span>{isMobile ? selectedModel?.name.split(' ')[0].slice(0, 7) : selectedModel?.name.split(' ')[0]}</span>
                {!isMobile && selectedModelBadge && <span style={{borderRadius: '6px', padding: selectedModelBadge.includes('️') ? '4px 8px' : '1px 5px', color: selectedModelBadge.includes('️') ? '#a882ff' : brand.gold, background: selectedModelBadge.includes('️') ? 'rgba(168,130,255,0.12)' : 'rgba(240,208,64,0.08)', fontSize: selectedModelBadge.includes('️') ? '16px' : '12px', fontWeight: 700, lineHeight: 1, border: selectedModelBadge.includes('️') ? 'none' : `1px solid ${brand.border}`}}>{selectedModelBadge}</span>}
                <span>▾</span>
                <span style={isMobile ? {position: 'absolute', right: 0, bottom: '50px', padding: '4px 7px', borderRadius: '6px', border: `1px solid ${brand.border}`, background: brand.bgElevated, color: brand.gold, fontSize: '10px', fontWeight: 700, letterSpacing: '0.01em', whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.24)'} : {marginLeft: '5px', paddingLeft: '9px', borderLeft: `1px solid ${brand.border}`, color: brand.gold, fontSize: '11px', fontWeight: 700, letterSpacing: '0.01em'}}>Try new OCR{isMobile ? ' ↓' : ''}</span>
              </button>
              {showModelDropdown && (
                <div style={{position: 'absolute', bottom: '50px', right: 0, background: brand.bgElevated, border: `1px solid ${brand.border}`, borderRadius: '12px', padding: '4px 0', minWidth: '220px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', zIndex: 100}}>
                  {CONFIG.models.map(m => (
                    <MenuItem
                      key={m.id}
                      label={m.name}
                      badge={'badge' in m ? m.badge : undefined}
                      info={m.mode === 'ocr'
                        ? 'Відкриється в новій вкладці. Поточна розмова залишиться в цьому чаті.'
                        : ('description' in m ? m.description : undefined)}
                      active={model === m.id}
                      href={m.mode === 'ocr' ? modelChatPath(m.id) : undefined}
                      onClick={m.mode === 'ocr' ? () => setShowModelDropdown(false) : () => selectModel(m)}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Send / Stop */}
            <button
              onClick={() => { if (isStreaming) { abortRef.current?.abort(); setIsStreaming(false); } else { sendMessage(); } }}
              disabled={!isStreaming && !input.trim() && attachments.length === 0}
              className="touch-target"
              style={{width: '44px', height: '44px', borderRadius: '8px', border: 'none', cursor: 'pointer', background: isStreaming ? brand.error : (input.trim() || attachments.length > 0) ? 'linear-gradient(135deg, #8B2A15, #B85520, #C48A20)' : brand.bgMuted, color: '#fff', fontSize: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease', flexShrink: 0, animation: isStreaming ? 'stopPulse 1.5s infinite' : 'none'}}
            >{isStreaming ? '■' : '↑'}</button>
          </div>
        </div>
      </div>

      {/* Hidden inputs */}
      <input ref={imageInputRef} type="file" accept="image/*" multiple hidden onChange={handleFileChange('image')} />
      <input ref={fileInputRef} type="file" accept={CONFIG.acceptedFiles} multiple hidden onChange={handleFileChange('file')} />

      {selectedConnector && (
        <ConnectorConfigModal connector={selectedConnector} onClose={() => setSelectedConnector(null)} />
      )}
      {infoConnector && (
        <ConnectorInfoModal connector={infoConnector} onClose={() => setInfoConnector(null)} />
      )}
    </div>
  );
}

interface ConnectorInfo {
  id: string;
  stateKey: ConnectorKey;
  name: string;
  label: string;
  icon: string;
  description: string;
  capabilities: string[];
  settings: {label: string; value: string}[];
  githubUrl: string;
  authorName: string;
  authorLinkedIn: string;
}

const MCP_CONNECTORS: ConnectorInfo[] = [
  {
    id: 'prozorro',
    stateKey: 'prozorro',
    name: 'Prozorro',
    label: 'Тендери та закупівлі',
    icon: '🔎',
    description: 'Конектор для пошуку публічних закупівель і тендерів у Prozorro за назвою організації, ЄДРПОУ або контекстом запиту.',
    capabilities: ['Пошук тендерів за компанією', 'Пошук закупівель у Prozorro', 'Робота з назвами організацій та ЄДРПОУ'],
    settings: [{label: 'Джерело даних', value: 'Публічні дані Prozorro'}],
    githubUrl: 'https://github.com/VladyslavMykhailyshyn/prozorro-mcp-server',
    authorName: 'Vladyslav Mykhailyshyn',
    authorLinkedIn: 'https://www.linkedin.com/in/vladyslav-mykhailyshyn-ph-d-0110bbba/',
  },
  {
    id: 'ukrainian-stats',
    stateKey: 'stats',
    name: 'Держстат України',
    label: 'Статистичні дані',
    icon: '📊',
    description: 'Конектор для роботи з публічними статистичними наборами даних України, включно зі списком доступних наборів і запитами до dataflow.',
    capabilities: ['Список доступних статистичних наборів', 'Отримання даних за dataflow ID', 'Перевірка доступності статистичних даних'],
    settings: [{label: 'Джерело даних', value: 'Публічна статистика України'}],
    githubUrl: 'https://github.com/VladyslavMykhailyshyn/ukrainian-stats-mcp-server',
    authorName: 'Vladyslav Mykhailyshyn',
    authorLinkedIn: 'https://www.linkedin.com/in/vladyslav-mykhailyshyn-ph-d-0110bbba/',
  },
];

function ConnectorMenuItem({connector, active, onToggle, onInfo, onConfig}: {connector: ConnectorInfo; active: boolean; onToggle: () => void; onInfo: () => void; onConfig: () => void}) {
  return (
    <div style={{display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '0 8px 0 42px', background: active ? 'rgba(212,165,74,0.08)' : 'transparent', transition: 'background 0.1s'}}>
      <button
        type="button"
        onClick={onToggle}
        aria-pressed={active}
        style={{display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, padding: '10px 0', border: 'none', background: 'transparent', color: brand.cream, fontSize: '14px', cursor: 'pointer', textAlign: 'left', fontFamily, outline: 'none'}}
      >
        <span style={{width: '18px', flexShrink: 0, textAlign: 'center'}}>{connector.icon}</span>
        <span style={{flex: 1, minWidth: 0}}>
          <span style={{display: 'block', fontSize: '14px', color: brand.cream, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{connector.name}</span>
          <span style={{display: 'block', fontSize: '12px', color: brand.creamMuted, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{connector.label}</span>
        </span>
        <span
          aria-hidden="true"
          style={{width: '34px', height: '20px', borderRadius: '999px', background: active ? 'rgba(212,165,74,0.18)' : 'rgba(255,255,255,0.05)', border: `1px solid ${active ? 'rgba(212,165,74,0.34)' : brand.border}`, position: 'relative', flexShrink: 0}}
        >
          <span style={{position: 'absolute', top: '3px', left: active ? '17px' : '3px', width: '12px', height: '12px', borderRadius: '50%', background: active ? brand.gold : brand.creamSubtle, transition: 'left 0.14s ease, background 0.14s ease'}} />
        </span>
      </button>
      <button
        type="button"
        onClick={onInfo}
        aria-label={`Інструкція ${connector.name}`}
        title="Інструкція"
        style={{width: '28px', height: '28px', borderRadius: '8px', border: `1px solid ${brand.border}`, background: 'transparent', color: brand.tealLight, cursor: 'pointer', flexShrink: 0, fontWeight: 800, fontFamily}}
      >i</button>
      <button
        type="button"
        onClick={onConfig}
        aria-label={`Конфіг ${connector.name}`}
        title="Конфіг"
        style={{width: '28px', height: '28px', borderRadius: '8px', border: `1px solid ${brand.border}`, background: 'transparent', color: brand.gold, cursor: 'pointer', flexShrink: 0}}
      >⚙</button>
    </div>
  );
}

function ConnectorHelpPill({connector, onClick}: {connector: ConnectorInfo; onClick: () => void}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{marginTop: '10px', display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '5px 10px', borderRadius: '999px', border: `1px solid ${brand.borderLight}`, background: 'rgba(255,255,255,0.035)', color: brand.creamMuted, fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily}}
      title={`Інструкція ${connector.name}`}
    >
      <span style={{color: brand.tealLight, fontWeight: 800}}>i</span>
      Довідка по конектору {connector.name}
    </button>
  );
}

function ConnectorInfoModal({connector, onClose}: {connector: ConnectorInfo; onClose: () => void}) {
  const [manual, setManual] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setManual('');

    fetch(`${CONFIG.backendUrl}/connectors/${connector.stateKey}/manual`)
      .then(async resp => {
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (typeof data.markdown !== 'string') throw new Error('Invalid manual response');
        if (!cancelled) setManual(data.markdown);
      })
      .catch(() => {
        if (!cancelled) setError('Не вдалося завантажити інструкцію. Спробуйте оновити сторінку.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [connector.stateKey]);

  return (
    <div style={{position: 'fixed', inset: 0, zIndex: 230, background: 'rgba(5, 14, 16, 0.74)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px'}} onMouseDown={onClose}>
      <div onMouseDown={e => e.stopPropagation()} style={{width: '100%', maxWidth: '760px', maxHeight: '86vh', overflowY: 'auto', background: brand.bgElevated, border: `1px solid ${brand.borderLight}`, borderRadius: '16px', boxShadow: '0 24px 80px rgba(0,0,0,0.62)', padding: '20px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '16px', position: 'sticky', top: '-20px', background: brand.bgElevated, paddingTop: '2px', paddingBottom: '12px', zIndex: 1}}>
          <div>
            <div style={{fontSize: '12px', color: brand.tealLight, letterSpacing: '1.2px', textTransform: 'uppercase', fontWeight: 700}}>Connector manual</div>
            <h3 style={{margin: '5px 0 0', color: brand.cream, fontSize: '18px'}}>{connector.icon} {connector.name}</h3>
          </div>
          <button onClick={onClose} style={{border: `1px solid ${brand.border}`, background: brand.bgMuted, color: brand.creamMuted, borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '18px'}}>×</button>
        </div>

        {loading && <div style={{color: brand.creamMuted, fontSize: '14px', padding: '18px 0'}}>Завантажую інструкцію...</div>}
        {error && <div style={{color: brand.orange || brand.gold, fontSize: '14px', padding: '18px 0'}}>{error}</div>}
        {!loading && !error && (
          <div style={{color: brand.creamMuted, fontSize: '14px', lineHeight: 1.6}}>
            <Markdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({node, ...props}: any) => <a {...props} target="_blank" rel="noreferrer" style={{color: brand.tealLight, textDecoration: 'none', fontWeight: 700}} />,
                h1: ({node, ...props}: any) => <h1 {...props} style={{color: brand.cream, fontSize: '22px', lineHeight: 1.25, margin: '0 0 14px'}} />,
                h2: ({node, ...props}: any) => <h2 {...props} style={{color: brand.cream, fontSize: '17px', lineHeight: 1.3, margin: '22px 0 10px', borderTop: `1px solid ${brand.borderLight}`, paddingTop: '16px'}} />,
                h3: ({node, ...props}: any) => <h3 {...props} style={{color: brand.cream, fontSize: '15px', lineHeight: 1.35, margin: '18px 0 8px'}} />,
                p: ({node, ...props}: any) => <p {...props} style={{margin: '0 0 10px', color: brand.creamMuted}} />,
                ul: ({node, ...props}: any) => <ul {...props} style={{margin: '0 0 12px 18px', padding: 0, color: brand.creamMuted}} />,
                ol: ({node, ...props}: any) => <ol {...props} style={{margin: '0 0 12px 18px', padding: 0, color: brand.creamMuted}} />,
                li: ({node, ...props}: any) => <li {...props} style={{marginBottom: '6px'}} />,
                code: ({node, inline, ...props}: any) => <code {...props} style={{background: 'rgba(255,255,255,0.06)', border: `1px solid ${brand.borderLight}`, borderRadius: '6px', padding: inline ? '1px 5px' : '10px', color: brand.gold, fontSize: '13px', fontFamily: 'monospace', display: inline ? 'inline' : 'block', overflowX: 'auto'}} />,
                table: ({node, ...props}: any) => <div style={{overflowX: 'auto', margin: '12px 0'}}><table {...props} style={{width: '100%', borderCollapse: 'collapse', minWidth: '520px'}} /></div>,
                th: ({node, ...props}: any) => <th {...props} style={{border: `1px solid ${brand.borderLight}`, background: brand.bgMuted, color: brand.cream, padding: '8px 10px', textAlign: 'left', fontSize: '12px'}} />,
                td: ({node, ...props}: any) => <td {...props} style={{border: `1px solid ${brand.borderLight}`, color: brand.creamMuted, padding: '8px 10px', fontSize: '13px', verticalAlign: 'top'}} />,
                hr: () => <hr style={{border: 'none', borderTop: `1px solid ${brand.borderLight}`, margin: '18px 0'}} />,
              }}
            >
              {manual}
            </Markdown>
          </div>
        )}

      </div>
    </div>
  );
}

function ConnectorConfigModal({connector, onClose}: {connector: ConnectorInfo; onClose: () => void}) {
  return (
    <div style={{position: 'fixed', inset: 0, zIndex: 220, background: 'rgba(5, 14, 16, 0.74)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px'}} onMouseDown={onClose}>
      <div onMouseDown={e => e.stopPropagation()} style={{width: '100%', maxWidth: '560px', background: brand.bgElevated, border: `1px solid ${brand.borderLight}`, borderRadius: '16px', boxShadow: '0 24px 80px rgba(0,0,0,0.62)', padding: '20px'}}>
        <div style={{display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '16px'}}>
          <div>
            <div style={{fontSize: '12px', color: brand.gold, letterSpacing: '1.2px', textTransform: 'uppercase', fontWeight: 700}}>Connector config</div>
            <h3 style={{margin: '5px 0 0', color: brand.cream, fontSize: '18px'}}>{connector.name}</h3>
          </div>
          <button onClick={onClose} style={{border: `1px solid ${brand.border}`, background: brand.bgMuted, color: brand.creamMuted, borderRadius: '8px', width: '34px', height: '34px', cursor: 'pointer', fontSize: '18px'}}>×</button>
        </div>

        <p style={{margin: '0 0 10px', color: brand.creamMuted, fontSize: '14px', lineHeight: 1.65}}>{connector.description}</p>

        <div style={{display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '16px'}}>
          <a
            href={connector.authorLinkedIn}
            target="_blank"
            rel="noreferrer"
            style={{color: brand.tealLight, textDecoration: 'none', fontSize: '14px', fontWeight: 600}}
          >
            by {connector.authorName}
          </a>
          <a
            href={connector.githubUrl}
            target="_blank"
            rel="noreferrer"
            style={{border: `1px solid ${brand.border}`, background: brand.bgMuted, color: brand.cream, borderRadius: '999px', padding: '5px 10px', textDecoration: 'none', fontSize: '12px', fontWeight: 700}}
          >
            GitHub
          </a>
        </div>

        <div style={{color: brand.cream, fontWeight: 700, fontSize: '14px', marginBottom: '8px'}}>Можливості</div>
        <div style={{display: 'flex', flexDirection: 'column', gap: '7px', marginBottom: '16px'}}>
          {connector.capabilities.map(item => (
            <div key={item} style={{color: brand.creamMuted, fontSize: '14px', lineHeight: 1.45, display: 'flex', gap: '8px'}}>
              <span style={{color: brand.gold}}>•</span><span>{item}</span>
            </div>
          ))}
        </div>

        <div style={{color: brand.cream, fontWeight: 700, fontSize: '14px', marginBottom: '8px'}}>Налаштування</div>
        <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
          {connector.settings.map(setting => (
            <div key={setting.label} style={{display: 'grid', gridTemplateColumns: '130px 1fr', gap: '12px', padding: '9px 0', borderBottom: `1px solid ${brand.borderLight}`}}>
              <div style={{color: brand.creamSubtle, fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.8px'}}>{setting.label}</div>
              <div style={{color: brand.cream, fontSize: '14px', lineHeight: 1.5}}>{setting.value}</div>
            </div>
          ))}
          <div style={{color: brand.creamSubtle, fontSize: '12px', lineHeight: 1.5, marginTop: '4px'}}>
            Додаткових користувацьких налаштувань для цього конектора зараз немає.
          </div>
        </div>
      </div>
    </div>
  );
}

const _thinkingPhrases = ['Думаю...', 'Шукаю інформацію...', 'Готую відповідь...', 'Раджуся з джерелами...', 'Аналізую запит...', 'Формулюю думку...', 'Збираю дані...'];
const _researchPhrases = ['🔬 Глибоке дослідження...', '☕ Можете взяти каву — це займе хвилинку...', '📚 Аналізую джерела...', '🔍 Збираю дані з інтернету...', '🧠 Синтезую інформацію...', '📝 Готую детальний звіт...'];

function ThinkingText({isResearch}: {isResearch?: boolean}) {
  const phrases = isResearch ? _researchPhrases : _thinkingPhrases;
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const i = setInterval(() => setIdx(prev => (prev + 1) % phrases.length), isResearch ? 3000 : 2000);
    return () => clearInterval(i);
  }, [isResearch, phrases.length]);
  return <span style={{fontSize: '14px', color: brand.creamMuted, fontStyle: 'italic', animation: 'fadeIn 0.3s ease'}}>{phrases[idx]}</span>;
}

function SourcesPill({sources}: {sources: {title: string; url: string; snippet?: string}[]}) {
  const [expanded, setExpanded] = useState(false);
  const count = sources.length;
  const label = count === 1 ? 'джерело' : count < 5 ? 'джерела' : 'джерел';
  return (
    <div style={{marginTop: '10px', borderTop: '1px solid rgba(78,205,196,0.1)', paddingTop: '8px'}}>
      <button onClick={() => setExpanded(!expanded)} style={{display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 12px', borderRadius: '999px', border: '1px solid rgba(78,205,196,0.2)', background: 'rgba(78,205,196,0.06)', color: '#4ecdc4', fontSize: '12px', fontWeight: 500, cursor: 'pointer', fontFamily: 'Inter, sans-serif'}}>
        🌐 {count} {label} <span style={{fontSize: '10px', transform: expanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'}}>▾</span>
      </button>
      {expanded && (
        <div style={{marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px'}}>
          {sources.map((s, i) => {
            let hostname = '';
            try { hostname = new URL(s.url).hostname; } catch {}
            return (
              <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" style={{display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '8px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(78,205,196,0.08)', textDecoration: 'none'}}>
                <img src={`https://www.google.com/s2/favicons?domain=${hostname}&sz=32`} alt="" style={{width: '14px', height: '14px', borderRadius: '3px', flexShrink: 0}} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <div style={{flex: 1, minWidth: 0}}>
                  <div style={{fontSize: '12px', color: '#f5edd8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{s.title || hostname}</div>
                  <div style={{fontSize: '11px', color: 'rgba(245,237,216,0.4)'}}>{hostname}</div>
                </div>
                <span style={{fontSize: '10px', color: '#4ecdc4', background: 'rgba(78,205,196,0.1)', borderRadius: '4px', padding: '1px 5px', fontWeight: 700, flexShrink: 0}}>{i + 1}</span>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MenuItem({icon, label, active, badge, info, disabled, href, onClick}: {icon?: string; label: string; active?: boolean; badge?: string; info?: string; disabled?: boolean; href?: string; onClick: () => void}) {
  const [infoOpen, setInfoOpen] = useState(false);

  return (
    <div style={{width: '100%', background: active ? 'rgba(212,165,74,0.08)' : 'transparent', transition: 'background 0.1s', opacity: disabled ? 0.72 : 1}}>
      <div style={{display: 'flex', alignItems: 'center', gap: '8px', width: '100%', padding: '0 10px 0 0'}}>
        {href ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClick}
            aria-label={`${label} — відкрити в новій вкладці`}
            style={{display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, padding: '10px 0 10px 16px', color: brand.cream, fontSize: '14px', textDecoration: 'none', fontFamily}}
          >
            <span style={{display: 'flex', flexDirection: 'column', gap: '3px'}}>
              <span>{label} <span aria-hidden="true" style={{color: brand.tealLight}}>↗</span></span>
              <span style={{color: brand.creamMuted, fontSize: '11px'}}>У новій вкладці</span>
            </span>
          </a>
        ) : (
          <button
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            style={{display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0, padding: '10px 0 10px 16px', border: 'none', background: 'transparent', color: disabled ? brand.creamSubtle : brand.cream, fontSize: '14px', cursor: disabled ? 'not-allowed' : 'pointer', textAlign: 'left', fontFamily}}
          >
            {icon && <span>{icon}</span>}
            <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{label}</span>
            {active && !badge && !info && <span style={{marginLeft: 'auto', color: brand.gold, fontSize: '12px'}}>✓</span>}
          </button>
        )}
        {info && (
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); setInfoOpen(prev => !prev); }}
            aria-expanded={infoOpen}
            aria-label={`Пояснення: ${label}`}
            title="Показати опис"
            style={{width: '19px', height: '19px', borderRadius: '50%', border: `1px solid ${brand.border}`, color: brand.tealLight, background: infoOpen ? 'rgba(78,205,196,0.16)' : 'rgba(78,205,196,0.08)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 800, lineHeight: 1, cursor: 'pointer', flexShrink: 0, fontFamily}}
          >i</button>
        )}
        {badge && <span style={{border: `1px solid ${brand.border}`, borderRadius: '999px', padding: '2px 7px', color: brand.gold, background: 'rgba(240,208,64,0.08)', fontSize: '12px', fontWeight: 600, flexShrink: 0}}>{badge}</span>}
        {active && !badge && info && <span style={{color: brand.gold, fontSize: '12px', flexShrink: 0}}>✓</span>}
      </div>
      {info && infoOpen && (
        <div style={{padding: '0 16px 10px 16px', color: brand.creamMuted, fontSize: '12px', lineHeight: 1.45}}>
          {info}
        </div>
      )}
    </div>
  );
}

function FeedbackButtons({sessionId, model, messageContent, userMessage}: {sessionId: string; model: string; messageContent: string; userMessage: string}) {
  const key = `fb_${sessionId}_${messageContent.slice(0, 20)}`;
  const [state, setState] = useState<'none'|'up'|'down'|'commenting'>(() => {
    try { return (localStorage.getItem(key) as any) || 'none'; } catch { return 'none'; }
  });
  const [comment, setComment] = useState('');
  const [error, setError] = useState('');

  const send = async (rating: 'up'|'down', cmt?: string) => {
    try {
      setError('');
      const r = await fetch(`${CONFIG.backendUrl}/feedback`, {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify({session_id: sessionId, model, rating, comment: cmt || '', message_content: messageContent, user_message: userMessage})
      });
      if (!r.ok) {
        console.error('[Feedback] Server error:', r.status);
        setError('Не вдалося надіслати відгук. Спробуйте ще раз.');
        return false;
      }
    } catch (e) {
      console.error('[Feedback] Network error:', e);
      setError('Не вдалося надіслати відгук. Спробуйте ще раз.');
      return false;
    }
    try { localStorage.setItem(key, rating); } catch {}
    return true;
  };

  if (state === 'up') return <div style={{fontSize: '12px', color: brand.creamSubtle, marginTop: '6px'}}>👍 Дякую!</div>;
  if (state === 'down') return <div style={{fontSize: '12px', color: brand.creamSubtle, marginTop: '6px'}}>👎 Відгук надіслано</div>;

  if (state === 'commenting') {
    return (
      <div style={{marginTop: '6px', display: 'flex', gap: '6px', alignItems: 'center'}}>
        <input
          type="text" placeholder="Що не так?" value={comment} onChange={e => setComment(e.target.value)}
          onKeyDown={async e => { if (e.key === 'Enter' && comment.trim() && await send('down', comment)) setState('down'); }}
          style={{flex: 1, padding: '4px 8px', fontSize: '12px', borderRadius: '6px', border: `1px solid ${brand.border}`, background: 'rgba(0,0,0,0.2)', color: brand.cream, fontFamily: 'inherit', outline: 'none'}}
          autoFocus
        />
        <button onClick={async () => { if (comment.trim() && await send('down', comment)) setState('down'); }} style={{padding: '4px 8px', fontSize: '11px', borderRadius: '6px', border: 'none', background: 'rgba(196,92,74,0.2)', color: '#c45c4a', cursor: 'pointer', fontFamily: 'inherit'}}>Надіслати</button>
        <button onClick={() => setState('none')} style={{padding: '4px 6px', fontSize: '11px', border: 'none', background: 'transparent', color: brand.creamSubtle, cursor: 'pointer'}}>✕</button>
        {error && <span style={{fontSize: '12px', color: '#c45c4a'}}>{error}</span>}
      </div>
    );
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '6px', marginTop: '10px'}} className="feedback-btns">
      <span style={{fontSize: '12px', color: brand.creamSubtle}}>Оцініть відповідь</span>
      <div style={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
        <button
          type="button"
          onClick={async () => { if (await send('up')) setState('up'); }}
          style={{border: `1px solid ${brand.border}`, background: 'rgba(78,205,196,0.1)', color: brand.cream, cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: '5px 10px', borderRadius: '7px', fontFamily: 'inherit'}}
          title="Корисна відповідь"
          aria-label="Оцінити відповідь добре"
        >
          👍 Добре
        </button>
        <button
          type="button"
          onClick={() => setState('commenting')}
          style={{border: `1px solid ${brand.border}`, background: 'rgba(196,92,74,0.1)', color: brand.cream, cursor: 'pointer', fontSize: '12px', fontWeight: 600, padding: '5px 10px', borderRadius: '7px', fontFamily: 'inherit'}}
          title="Повідомити про проблему у відповіді"
          aria-label="Оцінити відповідь погано"
        >
          👎 Погано
        </button>
      </div>
      {error && <span style={{fontSize: '12px', color: '#c45c4a'}}>{error}</span>}
    </div>
  );
}
// build 1781732172
